import {isNil} from 'ramda';

import {
  type EngagementReport,
  type OnlineOfferScrollDepthMilestone,
} from '../types/EngagementReport';

export const HEARTBEAT_ACTIVE_TIME_MS = 10_000;
const MAX_REPORT_ACTIVE_TIME_MS = 21_600_000;
const TICK_INTERVAL_MS = 1_000;
const INITIAL_SCROLL_DEPTH: OnlineOfferScrollDepthMilestone = 0;

type EngagementTrackerArgs = {
  /**
   * Return the delivery promise so a failed heartbeat can re-arm the sticky
   * certificate-click flag. Returning nothing opts out of failure handling.
   */
  onHeartbeat: (report: EngagementReport) => Promise<void> | void;
  onFinalFlush: (report: EngagementReport) => void;
};

const clampActiveTimeMs = (activeTimeMs: number) =>
  Math.min(Math.max(activeTimeMs, 0), MAX_REPORT_ACTIVE_TIME_MS);

export const createEngagementTracker = (args: EngagementTrackerArgs) => {
  let isActive = false;
  let activeSince: number | null = null;
  let accumulatedMs = 0;
  let maxScrollDepth: OnlineOfferScrollDepthMilestone = INITIAL_SCROLL_DEPTH;
  let hasUnreportedChange = false;
  let hasPendingCertificateClick = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const foldActiveTime = () => {
    if (!isActive || isNil(activeSince)) {
      return;
    }

    const now = Date.now();
    accumulatedMs = clampActiveTimeMs(accumulatedMs + Math.max(now - activeSince, 0));
    activeSince = now;
  };

  const takeReport = (): EngagementReport => {
    foldActiveTime();

    const report = {
      activeTimeMs: clampActiveTimeMs(accumulatedMs),
      scrollDepth: maxScrollDepth,
      certificateClick: hasPendingCertificateClick,
    };

    accumulatedMs = 0;
    hasUnreportedChange = false;
    hasPendingCertificateClick = false;

    return report;
  };

  /**
   * A certificate click is a one-shot conversion signal, so it must outlive a
   * failed request: re-arm the flag and let the next heartbeat or the final
   * flush carry it. Active time is deliberately NOT restored — the backend sums
   * deltas, so re-sending a duration it may already have stored would double
   * count, whereas re-sending the boolean is idempotent.
   */
  const deliverHeartbeat = (report: EngagementReport) => {
    // Promise.resolve absorbs the `void` case, so callers that do not report
    // delivery outcomes simply never trigger the re-arm.
    Promise.resolve(args.onHeartbeat(report)).catch(() => {
      hasPendingCertificateClick = hasPendingCertificateClick || report.certificateClick;
    });
  };

  const tick = () => {
    foldActiveTime();

    if (accumulatedMs < HEARTBEAT_ACTIVE_TIME_MS) {
      return;
    }

    deliverHeartbeat(takeReport());
  };

  const startInterval = () => {
    if (!isNil(intervalId)) {
      return;
    }

    intervalId = setInterval(tick, TICK_INTERVAL_MS);
  };

  const start = () => {
    if (isActive) {
      startInterval();
      return;
    }

    isActive = true;
    activeSince = Date.now();
    startInterval();
  };

  const stop = () => {
    if (!isNil(intervalId)) {
      clearInterval(intervalId);
      intervalId = null;
    }

    isActive = false;
    activeSince = null;
    accumulatedMs = 0;
  };

  const pause = () => {
    if (!isActive) {
      return;
    }

    foldActiveTime();
    isActive = false;
    activeSince = null;
  };

  const resume = () => {
    if (isActive) {
      return;
    }

    isActive = true;
    activeSince = Date.now();
    startInterval();
  };

  const updateScrollDepth = (milestone: OnlineOfferScrollDepthMilestone) => {
    if (milestone <= maxScrollDepth) {
      return;
    }

    maxScrollDepth = milestone;
    hasUnreportedChange = true;
  };

  const reportCertificateClick = () => {
    hasPendingCertificateClick = true;
    deliverHeartbeat(takeReport());
  };

  const flushFinal = () => {
    foldActiveTime();

    const hasNothingToReport =
      clampActiveTimeMs(accumulatedMs) === 0 && !hasUnreportedChange && !hasPendingCertificateClick;

    if (hasNothingToReport) {
      return;
    }

    args.onFinalFlush(takeReport());
  };

  return {
    start,
    stop,
    pause,
    resume,
    updateScrollDepth,
    reportCertificateClick,
    flushFinal,
  };
};
